import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Car, LogIn, LogOutIcon, Loader2, Users, Bike, QrCode,
  Pencil, CalendarDays, Trophy, Ticket, ReceiptText
} from 'lucide-react';
import AppFooter from '../components/AppFooter';
import { getParkingPrice } from '../lib/parkingPrices';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
export default function DashboardPage() {
  const { isAdmin, user } = useAuth();
  // Solo caja puede finalizar estadías; operator, admin y super_admin ven la misma vista operativa.
  const showExitActions = user?.role === 'cashier';
  const [vehiclesInside, setVehiclesInside] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tariff, setTariff] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [plateInput, setPlateInput] = useState('');
  const [vehicleType, setVehicleType] = useState('auto');
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrPlate, setQrPlate] = useState('');
  const [editingPlate, setEditingPlate] = useState(null);
  const [editPlateValue, setEditPlateValue] = useState('');
  const [receiptViewer, setReceiptViewer] = useState(null);
  // v1.2.1 — Parking + optional event addon (no longer mutually exclusive)
  const [eventTypes, setEventTypes] = useState([]);
  const [addonEnabled, setAddonEnabled] = useState(false);
  const [addonEventId, setAddonEventId] = useState('');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    // Listen for kiosk events to auto-refresh
    const channel = new BroadcastChannel('parking_events');
    channel.onmessage = () => { fetchData(); };
    return () => { clearInterval(interval); channel.close(); };
    // fetchData is intentionally polled; recreating the interval when local
    // filters change would duplicate listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, summaryRes, tariffRes, eventsRes] = await Promise.all([
        axios.get(`${API}/vehicles/inside`, { withCredentials: true }),
        axios.get(`${API}/reports/summary?period=today`, { withCredentials: true }),
        axios.get(`${API}/tariff`, { withCredentials: true }),
        axios.get(`${API}/event-types?only_active=true`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setVehiclesInside(vehiclesRes.data);
      setSummary(summaryRes.data);
      setTariff(tariffRes.data);
      setEventTypes(eventsRes.data || []);
      // If addon list changed and the previously-selected addon is gone, reset
      if (addonEventId && !(eventsRes.data || []).some(e => e.id === addonEventId)) {
        setAddonEventId('');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Event addon configuration helpers (admin sets in Tariff settings) ──
  // v1.2.1: entry is ALWAYS "parking + optional event". No more exclusive selector.
  const addonMode = (tariff?.event_addon_mode || 'disabled');
  const addonAvailable = addonMode !== 'disabled' && eventTypes.length > 0;
  const addonRequired = addonMode === 'required';
  const addonLabel = (tariff?.event_addon_label || 'Activar evento');

  // Sync addon defaults when opening entry modal or when tariff changes
  useEffect(() => {
    if (!showEntryModal) return;
    if (addonMode === 'disabled') {
      setAddonEnabled(false);
      setAddonEventId('');
      return;
    }
    if (addonRequired) {
      setAddonEnabled(true);
      if (!addonEventId && tariff?.event_addon_default_id) {
        setAddonEventId(tariff.event_addon_default_id);
      }
    } else if (addonMode === 'optional' && tariff?.event_addon_default_id && !addonEventId) {
      setAddonEventId(tariff.event_addon_default_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEntryModal, addonMode, tariff?.event_addon_default_id]);

  const handleEntry = async () => {
    if (!plateInput.trim()) {
      toast.error('Ingresa una patente');
      return;
    }
    if (addonRequired && !addonEventId) {
      toast.error('La configuracion exige seleccionar un evento');
      return;
    }
    if (addonEnabled && !addonRequired && !addonEventId) {
      toast.error('Selecciona el tipo de evento');
      return;
    }
    setIsProcessing(true);
    try {
      // v1.2.1 — entry is always "parking + optional event addon"
      const payload = {
        plate: plateInput.toUpperCase(),
        entry_type: 'manual',
        vehicle_type: vehicleType,
        service_type: 'parking',
      };
      if ((addonEnabled || addonRequired) && addonEventId) {
        payload.addon_event_type_id = addonEventId;
      }

      const { data } = await axios.post(`${API}/vehicles/entry`, payload, { withCredentials: true });
      const memberLabel = data.is_member ? ' (SOCIO)' : '';
      const paidLabel = data.paid_at_entry ? ` — Cobrado: ${formatCurrency(data.amount_charged)}` : '';
      const eventLabel = data.event_addon_name ? ` [+ Evento: ${data.event_addon_name}]` : '';
      setLastAction({
        type: 'entry', plate: data.plate, is_member: data.is_member,
        paid_at_entry: data.paid_at_entry, amount_charged: data.amount_charged,
        parking_amount: data.parking_amount,
        event_amount: data.event_amount,
        total_amount: data.total_amount,
        service_type: data.service_type,
        event_addon_name: data.event_addon_name,
        event_addon_price: data.event_addon_price,
        time: new Date().toLocaleTimeString(),
      });
      toast.success(`Entrada registrada: ${data.plate}${memberLabel}${eventLabel}${paidLabel}`);
      setShowEntryModal(false);
      setPlateInput('');
      if (!addonRequired) {
        setAddonEnabled(false);
        setAddonEventId('');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar entrada');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExit = async (plate) => {
    const plateToExit = plate || plateInput;
    if (!plateToExit.trim()) {
      toast.error('Ingresa una patente');
      return;
    }
    setIsProcessing(true);
    try {
      const { data } = await axios.post(`${API}/vehicles/exit`, {
        plate: plateToExit.toUpperCase()
      }, { withCredentials: true });
      setLastAction({
        type: 'exit', plate: data.plate, amount: data.amount_charged,
        duration: data.duration_minutes, is_member: data.is_member,
        time: new Date().toLocaleTimeString()
      });
      const memberLabel = data.is_member ? ' (SOCIO - Sin cargo)' : '';
      toast.success(`Salida: ${data.plate} - ${formatCurrency(data.amount_charged)}${memberLabel}`);
      setShowExitModal(false);
      setPlateInput('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al registrar salida');
    } finally {
      setIsProcessing(false);
    }
  };

  const viewReceipt = async (recordId) => {
    try {
      const { data } = await axios.get(`${API}/vehicles/records/${recordId}/receipt`, { withCredentials: true });
      setReceiptViewer(data.receipt_image);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar el comprobante');
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount);
  };

  const estimateAmount = (vehicle) => {
    if (vehicle.is_member) return 0;
    return getParkingPrice(vehicle.vehicle_type, tariff);
  };

  const getVehicleAmountDisplay = (vehicle) => {
    if (vehicle.is_member && !(vehicle.event_addon_price > 0)) {
      return { kind: 'member' };
    }
    if (vehicle.paid_at_entry) {
      return { kind: 'paid', amount: vehicle.amount_charged || 0 };
    }
    if ((vehicle.amount_charged || 0) > 0) {
      return { kind: 'partial', amount: vehicle.amount_charged };
    }
    return { kind: 'estimate', amount: estimateAmount(vehicle) };
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col overflow-x-hidden">
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 flex-1 overflow-hidden box-border">
        {/* Stats Cards (operativos) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Card className="bg-slate-800 border-slate-700 min-w-0">
            <CardContent className="p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-slate-400 uppercase">Vehiculos</p>
              <p className="text-2xl sm:text-3xl font-bold text-white" data-testid="vehicles-count">{vehiclesInside.length}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Adentro ahora</p>
            </CardContent>
          </Card>
          {isAdmin() && (
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <p className="text-[10px] sm:text-xs text-slate-400 uppercase">Ingresos hoy</p>
                <p className="text-lg sm:text-2xl font-bold text-emerald-400 truncate" data-testid="revenue-today">{summary ? formatCurrency(summary.total_revenue) : '$0'}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">Total combinado</p>
              </CardContent>
            </Card>
          )}
          {isAdmin() && (
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-1 mb-0.5"><Car className="w-3 h-3 text-blue-400" /><p className="text-[10px] sm:text-xs text-blue-300 uppercase">Estac.</p></div>
                <p className="text-base sm:text-xl font-bold text-blue-300 truncate">{summary ? formatCurrency(summary.parking_revenue || 0) : '$0'}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">{summary?.parking_operations || 0} ops</p>
              </CardContent>
            </Card>
          )}
          {isAdmin() && (
            <Card className="bg-slate-800 border-slate-700 min-w-0">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-1 mb-0.5"><CalendarDays className="w-3 h-3 text-amber-400" /><p className="text-[10px] sm:text-xs text-amber-300 uppercase">Eventos</p></div>
                <p className="text-base sm:text-xl font-bold text-amber-300 truncate">{summary ? formatCurrency(summary.event_revenue || 0) : '$0'}</p>
                <p className="text-[10px] sm:text-xs text-slate-500">{summary?.event_operations || 0} tickets</p>
              </CardContent>
            </Card>
          )}
          <Card className="bg-slate-800 border-slate-700 min-w-0">
            <CardContent className="p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-slate-400 uppercase">Entradas</p>
              <p className="text-2xl sm:text-3xl font-bold text-white">{summary?.total_entries || 0}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Operaciones hoy</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700 min-w-0">
            <CardContent className="p-3 sm:p-4">
              <p className="text-[10px] sm:text-xs text-slate-400 uppercase">T. Prom.</p>
              <p className="text-xl sm:text-2xl font-bold text-white truncate">{summary ? formatDuration(Math.round(summary.average_duration_minutes)) : '0m'}</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Estadia media</p>
            </CardContent>
          </Card>
        </div>

        {/* Top event hoy (solo admin y si hay eventos vendidos) */}
        {isAdmin() && summary?.top_event && (
          <Card className="bg-gradient-to-r from-amber-900/30 to-amber-700/10 border-amber-700/40 mb-4 sm:mb-6">
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <Trophy className="w-7 h-7 text-amber-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] sm:text-xs text-amber-200 uppercase tracking-wide">Top evento del dia</p>
                <p className="text-base sm:text-lg font-bold text-white truncate">{summary.top_event.name}</p>
              </div>
              <div className="text-right">
                <p className="text-base sm:text-xl font-bold text-emerald-400 whitespace-nowrap">{formatCurrency(summary.top_event.revenue)}</p>
                <p className="text-[10px] sm:text-xs text-slate-400">{summary.top_event.tickets} tickets</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className={`grid gap-2 sm:gap-4 mb-4 sm:mb-8 ${showExitActions ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <Button onClick={() => setShowEntryModal(true)} className="h-14 sm:h-20 bg-emerald-600 hover:bg-emerald-700 text-white text-sm sm:text-lg font-bold" data-testid="entry-btn">
            <LogIn className="w-5 h-5 mr-1 sm:mr-3 flex-shrink-0" /><span className="truncate">ENTRADA</span>
          </Button>
          {showExitActions && (
            <Button onClick={() => setShowExitModal(true)} className="h-14 sm:h-20 bg-red-600 hover:bg-red-700 text-white text-sm sm:text-lg font-bold" data-testid="exit-btn">
              <LogOutIcon className="w-5 h-5 mr-1 sm:mr-3 flex-shrink-0" /><span className="truncate">FINALIZAR ESTADÍA</span>
            </Button>
          )}
        </div>

        {/* Last Action */}
        {lastAction && (
          <Card className={`mb-8 ${lastAction.type === 'entry' ? 'bg-emerald-900/50 border-emerald-700' : 'bg-red-900/50 border-red-700'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {lastAction.type === 'entry' ? <LogIn className="w-6 h-6 text-emerald-400" /> : <LogOutIcon className="w-6 h-6 text-red-400" />}
                  <div>
                    <p className="text-white font-bold text-lg">
                      {lastAction.type === 'entry' ? 'ENTRADA' : 'SALIDA'}: {lastAction.plate}
                      {lastAction.is_member && <Badge className="ml-2 bg-blue-600">SOCIO</Badge>}
                      {lastAction.service_type === 'event' && <Badge className="ml-2 bg-amber-600"><Ticket className="w-3 h-3 mr-1" />EVENTO</Badge>}
                      {lastAction.event_addon_name && lastAction.service_type !== 'event' && (
                        <Badge className="ml-2 bg-amber-600/80"><Ticket className="w-3 h-3 mr-1" />+ {lastAction.event_addon_name}</Badge>
                      )}
                    </p>
                    <p className="text-slate-400 text-sm">
                      {lastAction.time}
                      {lastAction.event_name ? ` | ${lastAction.event_name}` : ''}
                      {lastAction.amount_charged != null && ` | ${lastAction.is_member && lastAction.service_type !== 'event' && !lastAction.event_addon_price ? 'Sin cargo' : formatCurrency(lastAction.amount_charged)}`}
                      {lastAction.amount != null && ` | ${lastAction.is_member ? 'Sin cargo' : formatCurrency(lastAction.amount)} | ${formatDuration(lastAction.duration)}`}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setLastAction(null)} className="text-slate-400">x</Button>
                {lastAction.type === 'entry' && (
                  <Button size="sm" onClick={() => { setQrPlate(lastAction.plate); setShowQrModal(true); }} className="bg-slate-700 hover:bg-slate-600 ml-2" data-testid="show-qr-btn">
                    <QrCode className="w-4 h-4 mr-1" />QR
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vehicles Inside */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Car className="w-5 h-5" />
              Vehiculos en el Estacionamiento ({vehiclesInside.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-slate-400">Cargando...</div>
            ) : vehiclesInside.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Car className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay vehiculos en el estacionamiento</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 px-3 sm:py-3 sm:px-4 text-slate-400 font-medium text-xs sm:text-sm">Patente</th>
                      <th className="text-left py-2 px-3 sm:py-3 sm:px-4 text-slate-400 font-medium text-xs sm:text-sm">Tipo</th>
                      <th className="text-left py-2 px-3 sm:py-3 sm:px-4 text-slate-400 font-medium text-xs sm:text-sm">Entrada</th>
                      <th className="text-left py-2 px-3 sm:py-3 sm:px-4 text-slate-400 font-medium text-xs sm:text-sm">Tiempo</th>
                      <th className="text-left py-2 px-3 sm:py-3 sm:px-4 text-slate-400 font-medium text-xs sm:text-sm">Monto</th>
                      <th className="text-right py-2 px-3 sm:py-3 sm:px-4 text-slate-400 font-medium text-xs sm:text-sm">Accion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehiclesInside.map((vehicle) => (
                      <tr key={vehicle.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`vehicle-row-${vehicle.plate}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {editingPlate === vehicle.id ? (
                              <div className="flex items-center gap-1">
                                <Input value={editPlateValue} onChange={(e) => setEditPlateValue(e.target.value.toUpperCase())}
                                  className="bg-slate-600 border-slate-500 text-white font-mono h-8 w-28 uppercase text-sm px-2" autoFocus />
                                <Button size="sm" className="bg-emerald-600 h-8 px-2 text-xs" onClick={async () => {
                                  try {
                                    await axios.put(`${API}/vehicles/correct-plate`, { record_id: vehicle.id, new_plate: editPlateValue }, { withCredentials: true });
                                    toast.success(`Corregido: ${editPlateValue}`);
                                    setEditingPlate(null); fetchData();
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
                                }}>OK</Button>
                                <Button size="sm" variant="ghost" className="h-8 px-1 text-slate-400" onClick={() => setEditingPlate(null)}>X</Button>
                              </div>
                            ) : (
                              <span className="font-mono text-lg font-bold text-white bg-slate-700 px-3 py-1 rounded">{vehicle.plate}</span>
                            )}
                            {vehicle.receipt_available && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => viewReceipt(vehicle.id)}
                                className="h-7 w-7 p-0 text-emerald-400 hover:text-emerald-300"
                                title="Ver comprobante"
                                data-testid={`receipt-btn-${vehicle.plate}`}
                              >
                                <ReceiptText className="h-4 w-4" />
                              </Button>
                            )}
                            {vehicle.is_member && <Badge className="bg-blue-600 text-xs">SOCIO</Badge>}
                            {vehicle.service_type === 'event' && <Badge className="bg-amber-600 text-xs"><Ticket className="w-3 h-3 mr-1" />EVENTO</Badge>}
                            {vehicle.event_addon_name && vehicle.service_type !== 'event' && (
                              <Badge className="bg-amber-600/80 text-xs"><Ticket className="w-3 h-3 mr-1" />+EVENTO</Badge>
                            )}
                          </div>
                          {vehicle.member_name && <p className="text-xs text-blue-400 mt-1">{vehicle.member_name}</p>}
                          {vehicle.event_name && <p className="text-xs text-amber-300 mt-1 truncate max-w-[160px]">{vehicle.event_name}</p>}
                          {vehicle.event_addon_name && (
                            <p className="text-xs text-amber-300 mt-1 truncate max-w-[180px]">+ {vehicle.event_addon_name} ({formatCurrency(vehicle.event_addon_price || 0)})</p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className={vehicle.vehicle_type === 'moto' ? 'text-amber-400 border-amber-400/50' : 'text-blue-400 border-blue-400/50'}>
                            {vehicle.vehicle_type === 'moto' ? <Bike className="w-3 h-3 mr-1" /> : <Car className="w-3 h-3 mr-1" />}
                            {vehicle.vehicle_type === 'moto' ? 'Moto' : 'Auto'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-slate-300">
                          {new Date(vehicle.entry_time).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-amber-400 border-amber-400/50">
                            {formatDuration(vehicle.duration_minutes)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 font-semibold">
                          {(() => {
                            const amountDisplay = getVehicleAmountDisplay(vehicle);
                            if (amountDisplay.kind === 'member') {
                              return <span className="text-blue-400">Sin cargo</span>;
                            }
                            if (amountDisplay.kind === 'paid') {
                              return (
                                <span className="text-amber-400">
                                  Pagado ({formatCurrency(amountDisplay.amount)})
                                </span>
                              );
                            }
                            if (amountDisplay.kind === 'partial') {
                              return (
                                <span className="text-amber-400">
                                  Cobrado ({formatCurrency(amountDisplay.amount)})
                                </span>
                              );
                            }
                            return (
                              <span className="text-emerald-400">
                                {formatCurrency(amountDisplay.amount)}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="py-2 px-3 sm:py-3 sm:px-4 text-right space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingPlate(vehicle.id); setEditPlateValue(vehicle.plate); }} className="text-blue-400 hover:text-blue-300 h-7 w-7 p-0" data-testid={`edit-btn-${vehicle.plate}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setQrPlate(vehicle.plate); setShowQrModal(true); }} className="text-slate-300 border-slate-600 hover:bg-slate-700 h-7 w-7 p-0" data-testid={`qr-btn-${vehicle.plate}`}>
                            <QrCode className="w-3.5 h-3.5" />
                          </Button>
                          {showExitActions && (
                            <Button size="sm" onClick={() => handleExit(vehicle.plate)} className="bg-red-600 hover:bg-red-700 text-white text-xs h-7 px-2">
                              Finalizar
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {tariff && (
          <div className="mt-4 text-center text-slate-400 text-sm">
            Tarifas: Moto {formatCurrency(getParkingPrice('moto', tariff))} · Auto {formatCurrency(getParkingPrice('auto', tariff))}
          </div>
        )}
      </main>

      <AppFooter />

      {/* Entry Modal */}
      <Dialog open={showEntryModal} onOpenChange={setShowEntryModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md sm:max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <LogIn className="w-5 h-5 text-emerald-400" />
              Registrar Entrada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-slate-300 text-sm mb-2 block">Patente del vehiculo</label>
              <Input
                value={plateInput} onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
                placeholder="ABC123" className="bg-slate-700 border-slate-600 text-white text-2xl font-mono text-center h-14 uppercase"
                autoFocus data-testid="plate-input"
              />
            </div>
            <div>
              <label className="text-slate-300 text-sm mb-2 block">Tipo de vehiculo</label>
              <Select value={vehicleType} onValueChange={setVehicleType}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white event-select-trigger" data-testid="vehicle-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 event-select-content">
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="moto">Moto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ── v1.3.1: Optional event addon (admin-controlled) ── */}
            {addonAvailable && (
              <div className="rounded-xl border-2 border-amber-500/40 bg-amber-900/10 p-3 sm:p-4 entry-addon-block" data-testid="event-addon-block">
                <button
                  type="button"
                  onClick={() => !addonRequired && setAddonEnabled(v => !v)}
                  disabled={addonRequired}
                  className="addon-toggle-row w-full flex items-center justify-between gap-3"
                  data-testid="addon-toggle"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <CalendarDays className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <div className="text-left min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{addonLabel}</div>
                      <div className="text-[11px] text-amber-200/70">
                        {addonRequired ? 'Obligatorio por configuracion' : 'Suma evento al cobro de estacionamiento'}
                      </div>
                    </div>
                  </div>
                  <span
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors ${
                      (addonEnabled || addonRequired) ? 'bg-amber-500' : 'bg-slate-600'
                    } ${addonRequired ? 'opacity-80 cursor-not-allowed' : ''}`}
                    aria-checked={addonEnabled || addonRequired}
                    role="switch"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${
                        (addonEnabled || addonRequired) ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </span>
                </button>

                {(addonEnabled || addonRequired) && (
                  <div className="mt-3">
                    <label className="text-[11px] uppercase tracking-wide text-amber-200/80 mb-1.5 block">
                      Tipo de evento
                    </label>
                    <Select value={addonEventId} onValueChange={setAddonEventId}>
                      <SelectTrigger
                        className="bg-slate-700 border-slate-600 text-white event-select-trigger h-11"
                        data-testid="addon-event-select"
                      >
                        <SelectValue placeholder="Seleccionar evento..." />
                      </SelectTrigger>
                      <SelectContent
                        className="bg-slate-800 border-slate-700 text-white max-h-72 event-select-content"
                      >
                        {eventTypes.map(et => (
                          <SelectItem key={et.id} value={et.id}>
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full" style={{ background: et.color || '#10b981' }} />
                              <span>{et.name}</span>
                              <span className="text-emerald-400 ml-2 font-mono">{formatCurrency(et.price)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(addonEnabled || addonRequired) && !addonEventId && (
                      <p className="text-[11px] text-amber-300 mt-1.5">
                        Seleccioná un evento para continuar.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── v1.3.1: Total card always visible — parking + evento + total ── */}
            {(() => {
              const ev = eventTypes.find(e => e.id === addonEventId);
              const parkingNow = getParkingPrice(vehicleType, tariff);
              const eventNow = (addonEnabled || addonRequired) && ev ? (ev.price || 0) : 0;
              const totalNow = (parkingNow || 0) + eventNow;
              return (
                <div
                  className="entry-total-card rounded-xl bg-slate-900/60 border border-slate-700 p-3 sm:p-4"
                  data-testid="entry-total-breakdown"
                >
                  <div className="flex items-center justify-between text-slate-200">
                    <span className="flex items-center gap-2">
                      <Car className="w-4 h-4 text-blue-400" />
                      <span>Estacionamiento</span>
                    </span>
                    <span className="font-mono text-sm" data-testid="entry-parking-amount">
                      {formatCurrency(parkingNow)}
                    </span>
                  </div>
                  {eventNow > 0 && (
                    <div className="flex items-center justify-between text-slate-200 mt-2">
                      <span className="flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-amber-400" />
                        <span className="truncate">Evento ({ev?.name})</span>
                      </span>
                      <span className="font-mono text-amber-300 text-sm" data-testid="entry-event-amount">
                        {formatCurrency(eventNow)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                    <span className="text-slate-300 text-xs uppercase tracking-wide">
                      Total de la estadía
                    </span>
                    <span className="text-emerald-400 font-mono font-bold text-xl" data-testid="entry-total-amount">
                      {formatCurrency(totalNow)}
                    </span>
                  </div>
                  {parkingNow === null && eventNow > 0 && (
                    <p className="text-[10px] text-slate-500 mt-1.5 leading-tight">
                      El evento se cobra al entrar; el estacionamiento se computa por minuto al salir.
                    </p>
                  )}
                </div>
              );
            })()}

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
              <Button variant="outline" onClick={() => { setShowEntryModal(false); setPlateInput(''); }} className="sm:flex-1 border-slate-600 text-slate-300 h-11">
                Cancelar
              </Button>
              <Button
                onClick={handleEntry}
                disabled={isProcessing || ((addonEnabled || addonRequired) && !addonEventId)}
                className="sm:flex-1 bg-emerald-600 hover:bg-emerald-700 h-11 font-semibold"
                data-testid="confirm-entry-btn"
              >
                {isProcessing
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : ((addonEnabled || addonRequired) ? 'Confirmar evento y registrar entrada' : 'Confirmar entrada')
                }
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Modal — solo rol caja */}
      {showExitActions && (
        <Dialog open={showExitModal} onOpenChange={setShowExitModal}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold flex items-center gap-2">
                <LogOutIcon className="w-5 h-5 text-red-400" />
                Registrar Salida
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-slate-300 text-sm mb-2 block">Patente del vehiculo</label>
                <Input value={plateInput} onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
                  placeholder="ABC123" className="bg-slate-700 border-slate-600 text-white text-2xl font-mono text-center h-14 uppercase" autoFocus />
              </div>
              {vehiclesInside.length > 0 && (
                <div>
                  <p className="text-slate-400 text-sm mb-2">O selecciona:</p>
                  <div className="flex flex-wrap gap-2">
                    {vehiclesInside.slice(0, 8).map((v) => (
                      <Button key={v.id} variant="outline" size="sm" onClick={() => setPlateInput(v.plate)}
                        className={`border-slate-600 font-mono ${v.is_member ? 'text-blue-400' : 'text-slate-300'}`}>
                        {v.plate} {v.is_member && '(S)'}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setShowExitModal(false); setPlateInput(''); }} className="flex-1 border-slate-600 text-slate-300">Cancelar</Button>
                <Button onClick={() => handleExit()} disabled={isProcessing} className="flex-1 bg-red-600 hover:bg-red-700" data-testid="confirm-exit-btn">
                  {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Salida'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Receipt viewer */}
      <Dialog open={Boolean(receiptViewer)} onOpenChange={(open) => { if (!open) setReceiptViewer(null); }}>
        <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto border-slate-700 bg-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ReceiptText className="w-5 h-5 text-emerald-400" />
              Comprobante de pago
            </DialogTitle>
          </DialogHeader>
          {receiptViewer && (
            <img src={receiptViewer} alt="Comprobante de pago" className="max-h-[75dvh] w-full rounded-lg object-contain" />
          )}
        </DialogContent>
      </Dialog>

      {/* QR Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center justify-center gap-2">
              <QrCode className="w-5 h-5 text-emerald-400" />
              QR Ticket — {qrPlate}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {qrPlate && (
              <img src={`${API}/vehicles/qr/${qrPlate}`} alt={`QR ${qrPlate}`}
                className="mx-auto w-64 h-64 rounded bg-white p-2" data-testid="qr-image" />
            )}
            <p className="text-slate-400 text-sm mt-4">Presente este QR al salir</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
